"use client";

import { useMemo } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { pageVariants, staggerContainerVariants } from "@/lib/animations";
import PostCard from "@/components/ui/PostCard";
import PostCardSkeleton from "@/components/ui/PostCardSkeleton";
import type { BlogPostCardViewModel, PaginationInfo } from "@/types/blog";

interface BlogTagHomeProps {
    posts?: BlogPostCardViewModel[];
    pagination?: PaginationInfo;
    language?: string;
    defaultLanguage?: string;
    categoryName?: string | null;
    categorySlug?: string | null;
    tagSlug?: string | null;
    tagName?: string | null;
    paginationBasePath?: string;
    translations?: Record<string, string>;
}

const buildRange = (current: number, total: number, radius = 2): number[] => {
    const start = Math.max(1, current - radius);
    const end = Math.min(total, current + radius);
    const range: number[] = [];
    for (let i = start; i <= end; i += 1) {
        range.push(i);
    }
    return range;
};

const buildPageHref = (basePath: string, page: number): string => {
    const normalized = basePath.replace(/\/+$/, "") || "/";
    if (page <= 1) {
        return normalized || "/";
    }
    const prefix = normalized === "/" ? "" : normalized;
    return `${prefix}/${page}`.replace(/\/+/g, "/");
};

const translate = (
    translations: Record<string, string> | undefined,
    key: string,
    fallback: string
): string => translations?.[key] ?? fallback;

export default function BlogTagHome({
    posts,
    pagination,
    language,
    defaultLanguage,
    categoryName,
    tagSlug,
    tagName,
    paginationBasePath,
    translations,
}: BlogTagHomeProps) {
    // Güvenli defaults – GrapesJS preview veya eksik prop geldiğinde patlamasın
    const safePosts = posts ?? [];
    const safePagination: PaginationInfo = pagination ?? {
        page: 1,
        pageCount: 1,
        pageSize: safePosts.length || 10,
        total: safePosts.length,
    };

    const paginationPages = useMemo(
        () => buildRange(safePagination.page, safePagination.pageCount),
        [safePagination.page, safePagination.pageCount]
    );

    const basePath = paginationBasePath?.replace(/\/+$/, "") || "/";
    const pageTitle = tagName || categoryName || 'Blog';

    return (
        <motion.div
            className="w-9/12 mx-auto px-4 py-16"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            {/* Page Title */}
            {pageTitle && (
                <motion.h1
                    className="text-3xl font-bold mb-8 text-center"
                    variants={staggerContainerVariants}
                >
                    {pageTitle}
                </motion.h1>
            )}
            <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                variants={staggerContainerVariants}
            >
                {safePosts.length === 0 ? (
                    Array.from({ length: 6 }).map((_, index) => (
                        <PostCardSkeleton key={index} index={index} />
                    ))
                ) : (
                    <>
                        {safePosts.map((post, index) => (
                            <PostCard key={post.id ?? `${post.href}-${index}`} post={post} index={index} isInView={true} />
                        ))}
                    </>
                )}
            </motion.div>
            <motion.div
                className={`flex justify-center items-center gap-2 mt-12
                    [&_a]:flex [&_a]:items-center [&_a]:justify-center [&_a]:w-[50px] [&_a]:h-[50px]
                    [&_a]:border [&_a]:border-alsero-soft-white
                    [&_a]:transform-border [&_a]:ease-in-out [&_a]:duration-300
                    [&_a]:text-alsero-light-gray [&_a]:hover:text-alsero-dark-gray [&_a]:hover:border-alsero-dark-gray`}
                variants={staggerContainerVariants}
            >
                <motion.div whileHover={{ scale: safePagination.page > 1 ? 1.1 : 1 }} whileTap={{ scale: safePagination.page > 1 ? 0.9 : 1 }}>
                    <Link
                        href={buildPageHref(basePath, safePagination.page - 1)}
                        aria-label="Önceki sayfa"
                        className={`flex h-[50px] w-[50px] items-center justify-center ${safePagination.page <= 1 ? 'pointer-events-none opacity-50' : ''}`}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.29527 6.7124L5.28685 11.704C5.68054 12.0977 6.31905 12.0977 6.71275 11.704C7.10644 11.3103 7.10644 10.6718 6.71275 10.2781L2.43457 5.99995L6.71275 1.72181C7.10644 1.32812 7.10644 0.689613 6.71275 0.29592C6.31905 -0.0977728 5.68054 -0.0977728 5.28685 0.29592L0.29527 5.2875C-0.0984226 5.68021 -0.0984226 6.3187 0.29527 6.7124ZM6.29707 7.01466L10.26 11.1914C10.6582 11.6105 11.3031 11.6105 11.7013 11.1914C12.0996 10.7722 12.0996 10.0922 11.7013 9.67257L8.45889 6.25525L11.7013 2.83794C12.0996 2.41875 12.0996 1.7383 11.7013 1.31912C11.3031 0.89994 10.6582 0.89994 10.26 1.31912L6.29707 5.49585C5.89887 5.91501 5.89887 6.59499 6.29707 7.01466Z" fill="black"/>
                        </svg>
                    </Link>
                </motion.div>
                {paginationPages.map((pageNumber) => (
                    <motion.div
                        key={pageNumber}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        data-active={pageNumber === safePagination.page}
                        className="transition-colors duration-300"
                    >
                        <Link
                            href={buildPageHref(basePath, pageNumber)}
                            className={`flex h-[50px] w-[50px] items-center justify-center border ${pageNumber === safePagination.page ? 'border-alsero-first bg-alsero-first text-white' : 'border-alsero-soft-white'}`}
                        >
                            {pageNumber}
                        </Link>
                    </motion.div>
                ))}
                <motion.div whileHover={{ scale: safePagination.page < safePagination.pageCount ? 1.1 : 1 }} whileTap={{ scale: safePagination.page < safePagination.pageCount ? 0.9 : 1 }}>
                    <Link
                        href={buildPageHref(basePath, safePagination.page + 1)}
                        aria-label="Sonraki sayfa"
                        className={`flex h-[50px] w-[50px] items-center justify-center ${safePagination.page >= safePagination.pageCount ? 'pointer-events-none opacity-50' : ''}`}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.7047 5.2876L6.71315 0.296002C6.31946 -0.0976908 5.68095 -0.0976908 5.28725 0.296002C4.89356 0.689695 4.89356 1.3282 5.28725 1.7219L9.56543 6.00005L5.28725 10.2782C4.89356 10.6719 4.89356 11.3104 5.28725 11.7041C5.68095 12.0978 6.31946 12.0978 6.71315 11.7041L11.7047 6.7125C12.0984 6.31979 12.0984 5.6813 11.7047 5.2876ZM5.70293 4.98534L1.74004 0.808613C1.34183 0.38945 0.696855 0.38945 0.298652 0.808613C-0.0995507 1.22778 -0.0995507 1.90775 0.298652 2.32743L3.54111 5.74475L0.298652 9.16206C-0.0995507 9.58125 -0.0995507 10.2617 0.298652 10.6809C0.696855 11.1001 1.34183 11.1001 1.74004 10.6809L5.70293 6.50415C6.10113 6.08499 6.10113 5.40501 5.70293 4.98534Z" fill="black"/>
                        </svg>
                    </Link>
                </motion.div>
                </motion.div>
            </motion.div>
    );
}